"use client";

/**
 * "An organization transition is on screen — don't restructure the app yet."
 *
 * Membership drives structure here: `app-bootstrap` forces the create-or-join
 * dialog open, `settings/layout` unmounts its subtree and redirects, and the
 * danger-zone page swaps its dialog for a disabled button — all straight off
 * the live views. So the instant a delete lands, every one of them reacts
 * mid-flight and the dialog running the transition is ripped out of the DOM
 * with no confirmation and no exit.
 *
 * A holder (today: the delete-organization dialog, from the moment it submits)
 * freezes those decisions until it has shown its confirmation and left.
 *
 * An external store rather than context: the holder and its observers sit on
 * opposite sides of the tree, and the holder unmounts as its page navigates
 * away — releasing through its effect cleanup either way.
 */
import { useSyncExternalStore } from "react";

let holdCount = 0;
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

/** Freezes membership-driven layout decisions until released. */
export function holdOrgTransition(): () => void {
  holdCount += 1;
  emit();

  let released = false;
  return () => {
    if (released) return;
    released = true;
    // Clamped: a test-only reset (or a double release) must never leave the
    // count negative, which would swallow the next real hold.
    holdCount = Math.max(0, holdCount - 1);
    emit();
  };
}

export function isOrgTransitionHeld(): boolean {
  return holdCount > 0;
}

function subscribe(callback: () => void): () => void {
  listeners.add(callback);
  return () => {
    listeners.delete(callback);
  };
}

/** Server render never holds — the client owns this entirely. */
function getServerSnapshot(): boolean {
  return false;
}

export function useOrgTransitionHeld(): boolean {
  return useSyncExternalStore(
    subscribe,
    isOrgTransitionHeld,
    getServerSnapshot,
  );
}

/** Tests only — module state persists across renders by design. */
export function resetOrgTransitionHold(): void {
  holdCount = 0;
  emit();
}
